import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Textarea from '../components/Textarea';
import Modal from '../components/Modal';
import Table from '../components/Table';
import {
    getReservedUsernames,
    createReservedUsername,
    bulkCreateReservedUsernames,
    deleteReservedUsername,
} from '../utils/api';
import { formatDate } from '../utils/helpers';
import toast from 'react-hot-toast';

export default function ReservedUsernames() {
    const queryClient = useQueryClient();
    const [showAddModal, setShowAddModal] = useState(false);
    const [showBulkModal, setShowBulkModal] = useState(false);
    const [username, setUsername] = useState('');
    const [reason, setReason] = useState('');
    const [bulkUsernames, setBulkUsernames] = useState('');
    const [search, setSearch] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['reserved-usernames', search],
        queryFn: () => getReservedUsernames({ search }),
    });

    const createMutation = useMutation({
        mutationFn: () => createReservedUsername({ username, reason: reason || 'Reserved by admin' }),
        onSuccess: () => {
            toast.success('Username reserved successfully');
            queryClient.invalidateQueries(['reserved-usernames']);
            setShowAddModal(false);
            setUsername('');
            setReason('');
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    const bulkCreateMutation = useMutation({
        mutationFn: () => {
            const usernames = bulkUsernames.split('\n').filter((u) => u.trim());
            return bulkCreateReservedUsernames(usernames);
        },
        onSuccess: () => {
            toast.success('Usernames reserved successfully');
            queryClient.invalidateQueries(['reserved-usernames']);
            setShowBulkModal(false);
            setBulkUsernames('');
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (reservedId) => deleteReservedUsername(reservedId),
        onSuccess: () => {
            toast.success('Username unreserved successfully');
            queryClient.invalidateQueries(['reserved-usernames']);
        },
        onError: (error) => {
            toast.error(error.message);
        },
    });

    const columns = [
        {
            header: 'Username',
            accessor: 'username',
            render: (item) => (
                <span className="font-medium">@{item.username}</span>
            ),
        },
        {
            header: 'Reason',
            accessor: 'reason',
            render: (item) => (
                <span className="text-sm text-muted-light dark:text-muted-dark">
                    {item.reason || '-'}
                </span>
            ),
        },
        {
            header: 'Reserved By',
            accessor: 'reservedBy',
            render: (item) => (
                <span className="text-sm">
                    {item.reservedBy || 'Unknown'}
                </span>
            ),
        },
        {
            header: 'Reserved On',
            accessor: 'createdAt',
            render: (item) => <span className="text-sm">{formatDate(item.createdAt)}</span>,
        },
        {
            header: 'Actions',
            accessor: '_id',
            render: (item) => (
                <Button
                    size="small"
                    variant="danger"
                    onClick={(e) => {
                        e.stopPropagation();
                        deleteMutation.mutate(item._id);
                    }}
                    disabled={deleteMutation.isPending}
                >
                    <Trash2 size={16} />
                </Button>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold mb-2">Reserved Usernames</h1>
                    <p className="text-muted-light dark:text-muted-dark">
                        Manage reserved usernames for special accounts
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="secondary" onClick={() => setShowBulkModal(true)}>
                        Bulk Add
                    </Button>
                    <Button variant="primary" onClick={() => setShowAddModal(true)}>
                        <Plus size={20} />
                        Add Username
                    </Button>
                </div>
            </div>

            <Card>
                <div className="mb-6">
                    <Input
                        placeholder="Search usernames..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full md:w-96"
                    />
                </div>

                <Table
                    columns={columns}
                    data={data?.data || []}
                    loading={isLoading}
                    emptyState={{
                        title: 'No reserved usernames',
                        description: 'Add your first reserved username',
                    }}
                />
            </Card>

            {/* Add Modal */}
            <Modal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                title="Reserve Username"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => createMutation.mutate()}
                            loading={createMutation.isPending}
                            disabled={!username.trim()}
                        >
                            Reserve
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <Input
                        label="Username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Enter username (without @)..."
                        required
                    />
                    <Input
                        label="Reason (optional)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why is this username reserved?"
                    />
                </div>
            </Modal>

            {/* Bulk Add Modal */}
            <Modal
                isOpen={showBulkModal}
                onClose={() => setShowBulkModal(false)}
                title="Bulk Reserve Usernames"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowBulkModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => bulkCreateMutation.mutate()}
                            loading={bulkCreateMutation.isPending}
                            disabled={!bulkUsernames.trim()}
                        >
                            Reserve All
                        </Button>
                    </>
                }
            >
                <Textarea
                    label="Usernames"
                    value={bulkUsernames}
                    onChange={(e) => setBulkUsernames(e.target.value)}
                    placeholder="Enter one username per line..."
                    rows={10}
                    helperText="Enter one username per line (without @)"
                    required
                />
            </Modal>
        </div>
    );
}

